import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, ArrowRight, ChevronRight, Check, Home as HomeIcon } from "lucide-react";
import { COMPANY, CITIES } from "../lib/constants";
import { SERVICE_PAGES } from "../lib/servicePages";
import { serviceCitySeo, serviceCityPath, cityPath, findNeighbors } from "../lib/seoPages";
import useSeo from "../lib/useSeo";
import ArtisanSection from "../components/ArtisanSection";

const CallButton = ({ testId }) => (
  <a
    data-testid={testId}
    href={`tel:${COMPANY.phoneDigits}`}
    className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-7 py-4 shadow-xl shadow-orange-600/30 text-lg transition-colors"
  >
    <Phone className="w-5 h-5" /> {COMPANY.phone}
  </a>
);

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
    <span className="h-px w-8 bg-teal-700" /> {children}
  </div>
);

const Hero = ({ service, city, seo }) => (
  <section className="max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
    <div className="lg:col-span-7 space-y-6">
      <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
        <MapPin className="w-3.5 h-3.5" /> {city.name} · {city.postal}
      </div>
      <h1 data-testid="service-city-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
        {service.name} à <span className="text-teal-600">{city.name}</span>
      </h1>
      <p data-testid="service-city-intro" className="text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed" dangerouslySetInnerHTML={{ __html: seo.intro }} />
      <div className="flex flex-wrap gap-3">
        <CallButton testId="service-city-cta-call" />
        <Link data-testid="service-city-cta-contact" to="/contact" className="inline-flex items-center gap-2 bg-white border-2 border-slate-900 text-slate-900 font-bold rounded-full px-6 py-4 hover:bg-slate-900 hover:text-white transition-colors">
          Devis gratuit <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
    <div className="lg:col-span-5 rounded-3xl bg-slate-900 text-white p-8">
      <div className="text-xs font-bold uppercase tracking-widest text-teal-300 mb-1">Depuis Mont-Dol</div>
      <div data-testid="service-city-distance" className="font-display text-4xl md:text-5xl font-extrabold">
        {city.distance === 0 ? "Sur place" : `~${city.distance} km`}
      </div>
      <div className="mt-6 flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl p-4">
        <Clock className="w-5 h-5 text-teal-300" />
        <div>
          <div data-testid="service-city-time" className="font-display text-xl font-extrabold">{seo.time}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Arrivée estimée · 24h/24</div>
        </div>
      </div>
      <div className="mt-4 text-sm text-slate-300">À partir de <strong className="text-amber-300 text-lg">{service.fromPrice} €</strong>, prix annoncé avant l'intervention.</div>
    </div>
  </section>
);

const Prices = ({ service, city }) => (
  <div data-testid="service-city-prices" className="bg-slate-900 text-white rounded-3xl p-7 md:p-9">
    <h2 className="font-display text-2xl font-extrabold mb-5">Tarifs {service.name.toLowerCase()} à {city.name}</h2>
    <ul className="divide-y divide-white/10">
      {service.prices.map(([label, price]) => (
        <li key={label} className="flex justify-between gap-4 py-3 text-sm md:text-base">
          <span className="text-slate-300">{label}</span>
          <span className="font-bold text-amber-300 whitespace-nowrap">{price}</span>
        </li>
      ))}
    </ul>
    <p className="text-xs text-slate-400 mt-4">Tarifs publics TTC. Majorations nuit, dimanche et jours fériés annoncées avant l'intervention.</p>
  </div>
);

const LinkCard = ({ to, testId, title, sub }) => (
  <Link to={to} data-testid={testId} className="group flex items-center justify-between gap-3 bg-white border border-slate-200 hover:border-teal-500 hover:shadow-md transition-shadow rounded-2xl p-4">
    <div>
      <div className="font-display font-bold text-slate-900 group-hover:text-teal-700">{title}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
    <ArrowRight className="w-4 h-4 text-teal-600 group-hover:translate-x-1 transition-transform" />
  </Link>
);

export default function ServiceCityPage({ serviceSlug, citySlug }) {
  const service = SERVICE_PAGES.find((s) => s.slug === serviceSlug);
  const city = CITIES.find((c) => c.slug === citySlug);
  const seo = serviceCitySeo(service, city);
  useSeo({ title: seo.title, description: seo.description, path: seo.path, jsonLd: seo.jsonLd });
  const neighbors = findNeighbors(city);
  const others = SERVICE_PAGES.filter((s) => s.slug !== service.slug);

  return (
    <div data-testid={`page-service-${service.slug}-${city.slug}`}>
      <nav aria-label="Fil d'Ariane" className="max-w-7xl mx-auto px-4 md:px-8 pt-8 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-teal-700"><HomeIcon className="w-3.5 h-3.5" /> Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to={cityPath(city)} data-testid="service-city-breadcrumb-city" className="hover:text-teal-700">Serrurier {city.name}</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-slate-900 font-semibold">{service.name}</span>
      </nav>

      <Hero service={service} city={city} seo={seo} />

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-8">
          {seo.sections.map((s, i) => (
            <div key={s.h} data-testid={`service-city-section-${i + 1}`}>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-3">{s.h}</h2>
              <p className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: s.p }} />
            </div>
          ))}
          <ul className="space-y-2 pt-2">
            {["Artisan local basé à Mont-Dol, sans sous-traitance", "Prix annoncé au téléphone avant de partir", "Facture détaillée pour votre assurance", "Disponible 24h/24, 7j/7"].map((it) => (
              <li key={it} className="flex items-center gap-3 text-slate-700"><Check className="w-4 h-4 text-teal-600" /> {it}</li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-5"><Prices service={service} city={city} /></div>
      </section>

      <ArtisanSection />

      <section data-testid="service-city-faq" className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <Eyebrow>Questions fréquentes</Eyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-8">{service.name} à {city.name} : vos questions</h2>
        <div className="space-y-3">
          {seo.faq.map((it, i) => (
            <details key={it.q} data-testid={`service-city-faq-${i + 1}`} open={i === 0} className="group rounded-3xl border border-slate-200 bg-white p-6 open:border-teal-500">
              <summary className="flex items-start justify-between gap-4 cursor-pointer list-none font-display text-lg font-bold text-slate-900">
                {it.q}
                <ChevronRight className="w-5 h-5 mt-1 flex-shrink-0 text-slate-500 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-slate-700 leading-relaxed">{it.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16 space-y-10">
        <div>
          <Eyebrow>Nos autres services à {city.name}</Eyebrow>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <LinkCard to={cityPath(city)} testId="service-city-link-city" title={`Serrurier ${city.name}`} sub="Tous nos services" />
            {others.map((s) => (
              <LinkCard key={s.slug} to={serviceCityPath(s, city)} testId={`service-city-other-${s.slug}`} title={`${s.name} ${city.name}`} sub={`Dès ${s.fromPrice} €`} />
            ))}
          </div>
        </div>
        {neighbors.length > 0 && (
          <div>
            <Eyebrow>{service.name} près de {city.name}</Eyebrow>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {neighbors.map((n) => (
                <LinkCard key={n.slug} to={serviceCityPath(service, n)} testId={`service-city-neighbor-${n.slug}`} title={`${service.name} ${n.name}`} sub={`${n.postal} · ${n.dept}`} />
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-teal-700 via-teal-800 to-slate-900 p-10 md:p-14 text-white flex flex-col md:flex-row md:items-center justify-between gap-6">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">Besoin d'un serrurier à {city.name} ?</h2>
          <CallButton testId="service-city-final-call" />
        </div>
      </section>
    </div>
  );
}
